import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from '../components/auth/LoginModal';

const Login: React.FC = () => {
  const { currentUser, isLoading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get the intended destination from navigation state
  const from = location.state?.from?.pathname || '/home';

  // If user is already logged in, redirect to intended destination
  useEffect(() => {
    if (currentUser && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [currentUser, isLoading, navigate, from]);

  // Auto-open the login modal when the page loads (only if not logged in)
  useEffect(() => {
    if (!currentUser && !isLoading) {
      setIsModalOpen(true);
    }
  }, [currentUser, isLoading]);

  // Handle successful login
  const handleLoginSuccess = () => {
    setIsModalOpen(false);
    navigate(from, { replace: true });
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img src="/Final logo file-04.svg" alt="Spectacle" className="h-16 w-16" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          Welcome back
        </h2>
        <p className="mt-2 text-center text-sm text-gray-400">
          Sign in to continue to Spectacle
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="backdrop-blur-md bg-[#272727] py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}

          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-500 hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
          >
            Sign in
          </button>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 backdrop-blur-md bg-[#272727] text-gray-400">Or</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link to="/register" className="text-sm font-medium text-purple-500 hover:text-purple-400 transition-colors">
                Create a new account
              </Link>
            </div>
          </div>
        </div>
      </div>

      <LoginModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default Login;